import * as Sentry from '@sentry/react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { App } from './App'
import { SidebarLayout } from './components/layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RequireRole } from './components/RequireRole'
import { RouteErrorFallback } from './components/RouteErrorFallback'
import { EntityTypeListPage, SearchDetailPage, SearchPage } from './features/search'
import AdminAudioPage from './pages/admin/audio'
import AdminLabelsPage from './pages/admin/labels'
import AdminProcessingPage from './pages/admin/processing'
import SchemaPlaygroundPage from './pages/admin/schema-playground'
import AdminUsersPage from './pages/admin/users'
import AdminVideoPage from './pages/admin/video'
import CaseLanding from './pages/cases/CaseLanding'
import CaseLayout from './pages/cases/CaseLayout'
import InvestigateLayout from './pages/cases/InvestigateLayout'
import CaseIdentityDetailPage from './pages/cases/identity-detail'
import CasesPage from './pages/cases/index'
import CaseCommsPage from './pages/cases/pages/comms/index'
import DocumentsListPage from './pages/cases/pages/documents/index'
import DocumentsPipelinesPage from './pages/cases/pages/documents/pipelines'
import CaseEvidencePage from './pages/cases/pages/evidence/index'
import CaseHypothesisDetailPage from './pages/cases/pages/hypothesis-detail'
import AgentPage from './pages/cases/pages/investigate/agent'
import AiInsightsPage from './pages/cases/pages/investigate/ai-insights'
import EntityResolutionPage from './pages/cases/pages/investigate/entity-resolution'
import EntityResolutionAuditPage from './pages/cases/pages/investigate/entity-resolution-audit'
import InvestigateGeolocationPage from './pages/cases/pages/investigate/geolocation'
import GraphViewsPage from './pages/cases/pages/investigate/graph-views'
import IdentityPage from './pages/cases/pages/investigate/identity'
import KnowledgeGraphPage from './pages/cases/pages/investigate/knowledge-graph'
import InvestigateOverviewPage from './pages/cases/pages/investigate/overview'
import TimeAxisPage from './pages/cases/pages/investigate/time-axis'
import CaseInvestigationDetailPage from './pages/cases/pages/investigation-detail'
import CaseInvestigationsPage from './pages/cases/pages/investigations'
import MonitorPage from './pages/cases/pages/monitor'
import CaseOsintDetailPage from './pages/cases/pages/osint/detail'
import CaseOsintPage from './pages/cases/pages/osint/index'
import OverviewPage from './pages/cases/pages/overview'
import ReportAuditPage from './pages/cases/pages/report/audit'
import ReportEvidencePage from './pages/cases/pages/report/evidence'
import ReportSummaryPage from './pages/cases/pages/report/index'
import CaseSchemaPage from './pages/cases/pages/schema'
import CaseSettingsPage from './pages/cases/pages/settings'
import CaseStructuredIngestPage from './pages/cases/pages/structured-ingest/index'
import PipelinesPage from './pages/cases/pipelines'
import ReportLayout from './pages/cases/ReportLayout'
import SchemaPage from './pages/cases/schema'
import DemoPage from './pages/demo'
import DocsAuditSchema from './pages/docs/audit-schema'
import DocsBestPractices from './pages/docs/best-practices'
import DocsCaseManagement from './pages/docs/case-management'
import DocsDocumentProcessing from './pages/docs/document-processing'
import DocsEntityResolution from './pages/docs/entity-resolution'
import DocsGettingStarted from './pages/docs/getting-started'
import DocsGraphExplorer from './pages/docs/graph-explorer'
import DocsPage from './pages/docs/index'
import DocsInvestigationAnalysis from './pages/docs/investigation-analysis'
import DocsLongHorizonFile from './pages/docs/long-horizon-file'
import DocsNaturalLanguage from './pages/docs/natural-language'
import DocsPipelinesGeospatial from './pages/docs/pipelines/geospatial'
import DocsPipelines from './pages/docs/pipelines/index'
import DocsPipelinesTabular from './pages/docs/pipelines/tabular'
import DocsPipelinesText from './pages/docs/pipelines/text'
import DocsSchemaConventions from './pages/docs/schema-conventions'
import DocsTimelineView from './pages/docs/timeline-view'
import DocumentsPage from './pages/documents/index'
import GeocodingExtensionPage from './pages/extensions/geocoding'
import ExtensionsPage from './pages/extensions/index'
import OsintExtensionPage from './pages/extensions/osint'
import InfoPage from './pages/info'
import LoginPage from './pages/login'
import RegisterPage from './pages/register'
import SettingsAppearancePage from './pages/settings/appearance'
import SettingsLayout from './pages/settings/layout'
import SettingsPermissionsPage from './pages/settings/permissions'
import SettingsProfilePage from './pages/settings/profile'
import SettingsSftpPage from './pages/settings/sftp'

// Dev-only routes (excluded in prod builds via tree-shaking of `if`).
const devRoutes = import.meta.env.DEV
  ? [
      {
        path: 'dev/time-axis',
        async lazy() {
          const { default: Page } = await import('./pages/dev/TimeAxisPlayground')
          return { Component: Page }
        },
      },
    ]
  : []

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <RouteErrorFallback />,
    children: [
      // Public routes - no layout
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'info',
        element: <InfoPage />,
      },
      // Legacy marketing URL — superseded by the Xendrel /info landing page.
      {
        path: 'features-list',
        element: <Navigate to="/info" replace />,
      },
      ...devRoutes,
      // HUMINT without a case in the address: reopens the last case, or asks which one.
      {
        path: 'humint',
        async lazy() {
          const { default: Page } = await import('./pages/humint/entry')
          return { Component: Page }
        },
      },
      // HUMINT workspace (ADR-0047): its own responsive shell, beside the Sidebar Layout.
      // Lazy, so the desktop app never loads it and it never loads the desktop app.
      {
        path: 'cases/:caseId/humint',
        async lazy() {
          const { default: HumintLayout } = await import('./pages/humint/HumintLayout')
          return { Component: HumintLayout }
        },
        children: [
          { index: true, element: <Navigate to="home" replace /> },
          {
            path: 'home',
            async lazy() {
              const { default: Page } = await import('./pages/humint/home')
              return { Component: Page }
            },
          },
          {
            path: 'check',
            async lazy() {
              const { default: Page } = await import('./pages/humint/check')
              return { Component: Page }
            },
          },
          {
            // Text check (UC-2): the query stays in the page's memory, never in the URL.
            path: 'search',
            async lazy() {
              const { default: Page } = await import('./pages/humint/search')
              return { Component: Page }
            },
          },
          {
            // Subjekty: the case's workspace subjects, and enrol without a photo.
            path: 'subjects',
            async lazy() {
              const { default: Page } = await import('./pages/humint/subjects')
              return { Component: Page }
            },
          },
          {
            // The subject page (slice 01); the ref is a graph element id, URL-encoded.
            path: 'subjects/:subjectRef',
            async lazy() {
              const { default: Page } = await import('./pages/humint/subject')
              return { Component: Page }
            },
          },
          {
            // Výsluch, light (UC-8): the recorder of one subject; the recording goes up when it ends.
            path: 'subjects/:subjectRef/recording',
            async lazy() {
              const { default: Page } = await import('./pages/humint/recording-new')
              return { Component: Page }
            },
          },
          {
            // One recording: the player and the transcript by voice; the id is the capture's.
            path: 'recordings/:captureId',
            async lazy() {
              const { default: Page } = await import('./pages/humint/recording')
              return { Component: Page }
            },
          },
          {
            // Výsluchy (UC-8, light): the case's recordings, newest first.
            path: 'sessions',
            async lazy() {
              const { default: Page } = await import('./pages/humint/sessions')
              return { Component: Page }
            },
          },
          {
            // Výsluch of one encounter (ADR-0057): the recorder asks which of its subjects take part.
            path: 'encounters/:encounterRef/recording',
            async lazy() {
              const { default: Page } = await import('./pages/humint/recording-encounter')
              return { Component: Page }
            },
          },
          {
            // Stretnutia (SC-041): the case's encounters by day; the filters live in the address,
            // the capture tag never does.
            path: 'encounters',
            async lazy() {
              const { default: Page } = await import('./pages/humint/encounters')
              return { Component: Page }
            },
          },
          {
            // Tetovania (UC-7): the analyst's tattoo screen; `?mark=` opens one mark (an id).
            path: 'tattoos',
            async lazy() {
              const { default: Page } = await import('./pages/humint/tattoos')
              return { Component: Page }
            },
          },
          {
            // Every mark of the case, by filters and by its description in any language (SC-029).
            path: 'marks',
            async lazy() {
              const { default: Page } = await import('./pages/humint/marks')
              return { Component: Page }
            },
          },
          {
            // The voice comparison: `?pair=` names the pair of voiceprints it judges (an id).
            path: 'voices',
            async lazy() {
              const { default: Page } = await import('./pages/humint/voices')
              return { Component: Page }
            },
          },
          {
            path: ':section',
            async lazy() {
              const { default: Page } = await import('./pages/humint/placeholder')
              return { Component: Page }
            },
          },
        ],
      },
      // Protected routes - with Sidebar Layout
      {
        element: (
          <ProtectedRoute>
            <SidebarLayout />
          </ProtectedRoute>
        ),
        children: [
          // Data section
          { path: 'data', element: <DocumentsPage /> },
          // Extensions section
          { path: 'extensions', element: <ExtensionsPage /> },
          { path: 'extensions/geocoding', element: <GeocodingExtensionPage /> },
          { path: 'extensions/osint', element: <OsintExtensionPage /> },
          // Secondary pages
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              { path: 'profile', element: <SettingsProfilePage /> },
              { path: 'appearance', element: <SettingsAppearancePage /> },
              { path: 'sftp', element: <SettingsSftpPage /> },
              { path: 'permissions', element: <SettingsPermissionsPage /> },
              {
                path: 'audio',
                element: <Navigate to="/admin/audio" replace />,
              },
            ],
          },
          {
            path: 'admin/labels',
            element: (
              <RequireRole minRole="admin">
                <AdminLabelsPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/users',
            element: (
              <RequireRole minRole="admin">
                <AdminUsersPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/audio',
            element: (
              <RequireRole minRole="admin">
                <AdminAudioPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/video',
            element: (
              <RequireRole minRole="admin">
                <AdminVideoPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/processing',
            element: (
              <RequireRole minRole="admin">
                <AdminProcessingPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/schema',
            element: (
              <RequireRole minRole="admin">
                <SchemaPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/schema-playground',
            element: (
              <RequireRole minRole="admin">
                <SchemaPlaygroundPage />
              </RequireRole>
            ),
          },
          {
            path: 'admin/pipelines',
            element: (
              <RequireRole minRole="admin">
                <PipelinesPage />
              </RequireRole>
            ),
          },
          {
            path: 'docs',
            children: [
              { index: true, element: <DocsPage /> },
              { path: 'getting-started', element: <DocsGettingStarted /> },
              { path: 'extraction-demo', element: <DemoPage /> },
              { path: 'case-management', element: <DocsCaseManagement /> },
              {
                path: 'document-processing',
                element: <DocsDocumentProcessing />,
              },
              { path: 'graph-explorer', element: <DocsGraphExplorer /> },
              { path: 'natural-language', element: <DocsNaturalLanguage /> },
              { path: 'entity-resolution', element: <DocsEntityResolution /> },
              {
                path: 'pipelines',
                children: [
                  { index: true, element: <DocsPipelines /> },
                  { path: 'text', element: <DocsPipelinesText /> },
                  { path: 'tabular', element: <DocsPipelinesTabular /> },
                  { path: 'geospatial', element: <DocsPipelinesGeospatial /> },
                ],
              },
              {
                path: 'investigation-analysis',
                element: <DocsInvestigationAnalysis />,
              },
              { path: 'timeline-view', element: <DocsTimelineView /> },
              { path: 'long-horizon-file', element: <DocsLongHorizonFile /> },
              {
                path: 'schema-conventions',
                element: <DocsSchemaConventions />,
              },
              { path: 'best-practices', element: <DocsBestPractices /> },
              { path: 'audit-schema', element: <DocsAuditSchema /> },
            ],
          },
          // Cases section — static segments first so they win over `:caseId`
          { path: 'cases', element: <Navigate to="/cases/home" replace /> },
          { path: 'cases/home', element: <CasesPage /> },
          {
            path: 'cases/list',
            element: <Navigate to="/cases/home" replace />,
          },
          // Case identity detail — distinct page, outside CaseLayout
          {
            path: 'cases/:caseId/identity/:identityId',
            element: <CaseIdentityDetailPage />,
          },
          // Case detail — flat routes under CaseLayout
          {
            path: 'cases/:caseId',
            element: <CaseLayout />,
            children: [
              // Every kind of case opens on the same overview (ADR-0102).
              { index: true, element: <CaseLanding /> },
              { path: 'overview', element: <OverviewPage /> },
              { path: 'dashboard', element: <CaseLanding /> },
              { path: 'investigations', element: <CaseInvestigationsPage /> },
              {
                path: 'investigations/:investigationId',
                element: <CaseInvestigationDetailPage />,
              },
              // Online OSINT agent — list of investigations + detail (like the
              // deep-research surface, but sourcing live external registers).
              { path: 'comms', element: <CaseCommsPage /> },
              {
                path: 'structured-ingest',
                element: <CaseStructuredIngestPage />,
              },
              { path: 'osint', element: <CaseOsintPage /> },
              { path: 'osint/:osintId', element: <CaseOsintDetailPage /> },
              // Legacy research list — research is the single surface now
              // (DEEP_RESEARCH_REDESIGN.md phase 4); hypotheses are listed on Investigations.
              {
                path: 'hypotheses',
                element: <Navigate to="../search" replace />,
              },
              // A hypothesis (ADR-0095); an id no hypothesis answers to is a legacy research
              // link, and the page opens the research detail instead.
              {
                path: 'hypotheses/:hypothesisId',
                element: <CaseHypothesisDetailPage />,
              },
              { path: 'settings', element: <CaseSettingsPage /> },
              { path: 'schema', element: <CaseSchemaPage /> },
              { path: 'monitor', element: <MonitorPage /> },
              { path: 'documents', element: <DocumentsListPage /> },
              { path: 'extraction-runs', element: <DocumentsPipelinesPage /> },
              // Backward-compat for old nested documents/pipelines URL
              {
                path: 'documents/pipelines',
                element: <Navigate to="../extraction-runs" replace />,
              },
              // Report sub-views — flattened, with the InvestigateLayout-equivalent ReportLayout
              // wrapping each (ReportLayout previously wrapped via children).
              {
                path: 'report',
                element: <ReportLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="../case-summary" replace />,
                  },
                ],
              },
              {
                element: <ReportLayout />,
                children: [
                  { path: 'case-summary', element: <ReportSummaryPage /> },
                  { path: 'evidence', element: <ReportEvidencePage /> },
                  { path: 'audit', element: <ReportAuditPage /> },
                ],
              },
              // Investigate sub-views — flattened, sharing InvestigateLayout
              {
                path: 'investigate',
                element: <InvestigateLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="../insights" replace />,
                  },
                ],
              },
              {
                element: <InvestigateLayout />,
                children: [
                  { path: 'insights', element: <InvestigateOverviewPage /> },
                  // Hidden by design — not linked from the icon rail. Reachable
                  // by URL only for QA / dev / history browsing.
                  { path: 'ai-insights', element: <AiInsightsPage /> },
                  { path: 'explore-graph', element: <KnowledgeGraphPage /> },
                  {
                    path: 'knowledge-graph',
                    element: <Navigate to="../explore-graph" replace />,
                  },
                  { path: 'graph', element: <GraphViewsPage /> },
                  {
                    path: 'graph-views',
                    element: <Navigate to="../graph" replace />,
                  },
                  {
                    path: 'entity-resolution',
                    element: <EntityResolutionPage />,
                  },
                  {
                    path: 'entity-resolution/audit',
                    element: <EntityResolutionAuditPage />,
                  },
                  { path: 'identity', element: <IdentityPage /> },
                  {
                    path: 'geolocation',
                    element: <InvestigateGeolocationPage />,
                  },
                  { path: 'time-axis', element: <TimeAxisPage /> },
                  { path: 'search', element: <SearchPage /> },
                  {
                    path: 'search/entities/:typeLabel',
                    element: <EntityTypeListPage />,
                  },
                  {
                    path: 'search/:hypothesisId',
                    element: <SearchDetailPage />,
                  },
                ],
              },
              { path: 'evidence/:referenceId', element: <CaseEvidencePage /> },
              { path: 'agent', element: <AgentPage /> },
            ],
          },
        ],
      },
    ],
  },
])
